import { useEffect, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Modal,
  Text,
  TextField,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import "./TrasladoTicketsCard.css";
import FieldLinkShare, { unitLabel, gastosUrl } from "../Services/FieldLinkShare";

// Staff view of the trasladista's expense tickets for one traslado, plus the
// link they upload them from (/gastos/:token). Tickets become reimbursable
// once their CFDI arrives at the expenses mailbox and matches (next step).

export const KIND_LABEL = {
  gasolina: "Gasolina",
  peaje: "Caseta",
  regreso: "Regreso del chofer",
  herramienta: "Herramienta",
  otro: "Otro",
};
const STATUS = {
  pending_cfdi: { label: "Falta factura", tone: "attention" },
  cfdi_matched: { label: "Factura recibida", tone: "info" },
  needs_review: { label: "Por revisar", tone: "warning" },
  approved: { label: "Aprobado", tone: "success" },
  rejected: { label: "Rechazado", tone: "critical" },
  expired: { label: "Vencido", tone: undefined },
};

// A ticket must get its CFDI before the end of the month in which it can be
// invoiced — its own month, or the month of invoice_available_from for portals
// that only invoice days later (expire_traslado_tickets).
export const cfdiDeadline = (ticket) => {
  const base = ticket.invoice_available_from || ticket.ticket_date;
  const d = base ? new Date(`${base}T12:00:00`) : new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
};
const shortDate = (d) => d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
// True while the portal still can't issue the CFDI.
export const notYetInvoiceable = (ticket) =>
  !!ticket.invoice_available_from &&
  new Date(`${ticket.invoice_available_from}T00:00:00`) > new Date();
// Tickets can be added while the traslado is live.
const OPEN = ["authorized", "in_progress", "completed"];

function TrasladoTicketsCard({ order, canEdit, canReview, reloadKey, onChange }) {
  const [code, setCode] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    const [{ data: link }, { data: rows }] = await Promise.all([
      supabase
        .from("traslado_capture_links")
        .select("code")
        .eq("traslado_order", order.id)
        .is("revoked_at", null)
        .maybeSingle(),
      supabase
        .from("traslado_tickets")
        // The CFDI linked to each ticket (expense_cfdis.ticket_id).
        .select("*, cfdis:expense_cfdis(id, uuid, emisor_nombre, emisor_rfc, total, sat_estado, xml_path, pdf_path, source)")
        .eq("traslado_order", order.id)
        .order("created_at", { ascending: false }),
    ]);
    setCode(link?.code || null);
    setTickets(rows || []);
    if (rows?.length) {
      const { data: signed } = await supabase.storage
        .from("traslado-tickets")
        .createSignedUrls(rows.map((r) => r.photo_path), 3600);
      setUrls(Object.fromEntries((signed || []).map((x) => [x.path, x.signedUrl])));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, reloadKey]);

  const [rejecting, setRejecting] = useState(null);
  const [noCfdi, setNoCfdi] = useState(null); // ticket being approved without CFDI

  const openCfdiFile = async (path) => {
    const { data } = await supabase.storage.from("expense-cfdis").createSignedUrl(path, 600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };
  // A ticket can be paid with several CFDIs (a toll plaza shared by two issuers).
  const cfdisOf = (t) => t.cfdis || [];
  const cfdiSum = (t) => cfdisOf(t).reduce((s, c) => s + Number(c.total || 0), 0);

  // Generic RPC runner for the small per-ticket actions.
  const act = async (fn, args) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc(fn, args);
    setBusy(false);
    if (err) {
      setError(err.message);
      return false;
    }
    onChange?.();
    return true;
  };
  const approveNoCfdi = async () => {
    if (await act("approve_ticket_without_cfdi", { p_ticket: noCfdi.id, p_reason: reason })) {
      setNoCfdi(null);
      setReason("");
    }
  };
  const [reason, setReason] = useState("");
  const decide = async (ticket, approve) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("decide_traslado_ticket", {
      p_ticket: ticket.id,
      p_approve: approve,
      p_reason: approve ? null : reason,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setRejecting(null);
    setReason("");
    onChange?.();
  };

  const getLink = async (regenerate = false) => {
    setBusy(true);
    setError(null);
    const { data, error: err } = await supabase.rpc("traslado_capture_link", {
      p_order: order.id,
      p_regenerate: regenerate,
    });
    setBusy(false);
    if (err) setError(err.message);
    else setCode(data);
  };

  const open = OPEN.includes(order.status);
  const url = code ? gastosUrl(code) : null;
  const total = tickets.reduce((s, t) => s + Number(t.total || 0), 0);
  const pending = tickets.filter((t) => t.status === "pending_cfdi").length;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd">Tickets del trasladista</Text>
          {tickets.length > 0 && (
            <InlineStack gap="200">
              {pending > 0 && <Badge tone="attention">{`${pending} sin factura`}</Badge>}
              <Text fontWeight="semibold">{formatPrice(total)}</Text>
            </InlineStack>
          )}
        </InlineStack>
        {error && <Banner tone="critical">{error}</Banner>}

        {open && canEdit && !code && (
          <InlineStack>
            <Button loading={busy} onClick={() => getLink(false)}>
              Generar liga de gastos
            </Button>
          </InlineStack>
        )}
        {open && code && (
          <BlockStack gap="100">
            <FieldLinkShare
              url={url}
              label="Liga para subir tickets"
              message={`Hola${order.trasladista ? ` ${order.trasladista.name.split(" ")[0]}` : ""} 👋\n\nSube aquí la foto de cada ticket de gasolina, casetas o herramienta del traslado de la unidad *${unitLabel({ modelName: order.car?.model?.name, plate: order.car?.plate, year: order.car?.year })}* (${order.origin_label || ""} → ${order.dest_label || ""}) 🧾\n\n👉 ${url}\n\nAhí mismo vienen los datos para pedir la factura. ¡Gracias!`}
            />
            {canEdit && (
              <InlineStack>
                <Button variant="plain" loading={busy} onClick={() => getLink(true)}>
                  Generar nueva liga (invalida la anterior)
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        )}
        {!order.trasladista && open && (
          <Text tone="subdued" variant="bodySm">
            Asigna un trasladista al traslado para enviarle la liga por WhatsApp.
          </Text>
        )}

        {tickets.length === 0 ? (
          <Text tone="subdued">Sin tickets todavía.</Text>
        ) : (
          <BlockStack gap="200">
            {tickets.map((t) => (
              <InlineStack key={t.id} gap="300" blockAlign="center" wrap={false}>
                <a href={urls[t.photo_path]} target="_blank" rel="noreferrer">
                  <img
                    src={urls[t.photo_path]}
                    alt="ticket"
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, background: "#eee" }}
                  />
                </a>
                <BlockStack gap="050">
                  <InlineStack gap="200" blockAlign="center">
                    {/* Native select: Polaris popovers land off-screen once this
                        page's inner scroll container is scrolled. */}
                    <select
                      className="ticket-kind-select"
                      value={t.kind}
                      disabled={busy}
                      aria-label="Concepto"
                      onChange={(e) => act("set_ticket_kind", { p_ticket: t.id, p_kind: e.target.value })}
                    >
                      {Object.entries(KIND_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <Badge tone={STATUS[t.status]?.tone}>{STATUS[t.status]?.label || t.status}</Badge>
                    {t.status === "pending_cfdi" && (
                      <Badge tone={t.invoice_mode === "portal" ? "info" : undefined}>
                        {t.invoice_mode === "portal" ? "GoMove factura en portal" : "Trasladista factura en caja"}
                      </Badge>
                    )}
                    {t.status === "approved" && t.deductible === false && (
                      <Badge tone="warning">No deducible</Badge>
                    )}
                    {t.status === "approved" && t.cfdi_pending && (
                      <Badge tone="attention">Factura pendiente</Badge>
                    )}
                    {t.duplicate_of && (
                      <Badge tone="critical">Posible duplicado</Badge>
                    )}
                  </InlineStack>
                  <Text tone="subdued" variant="bodySm">
                    {[t.merchant_name, t.merchant_rfc, t.ticket_date, t.liters ? `${t.liters} L` : null]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </Text>
                  {(t.cfdi_uuid || cfdisOf(t).length > 0) && (
                    <BlockStack gap="050">
                      {(cfdisOf(t).length ? cfdisOf(t) : [{ uuid: t.cfdi_uuid }]).map((c, i) => (
                        <InlineStack key={c.uuid} gap="200" blockAlign="center">
                          {i === 0 &&
                            (t.cfdi_uuid ? (
                              <Badge tone="success">Con factura</Badge>
                            ) : (
                              <Badge tone="attention">{`Factura parcial: ${formatPrice(cfdiSum(t))} de ${formatPrice(t.total)}`}</Badge>
                            ))}
                          <Text tone="subdued" variant="bodySm">
                            {[
                              c.emisor_nombre || c.emisor_rfc,
                              cfdisOf(t).length > 1 || Math.abs(cfdiSum(t) - Number(t.total)) > 0.05
                                ? `factura ${formatPrice(c.total)}`
                                : null,
                              c.uuid?.slice(0, 8),
                              c.sat_estado && c.sat_estado !== "Vigente" ? `SAT: ${c.sat_estado}` : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                          {c.pdf_path && (
                            <Button variant="plain" onClick={() => openCfdiFile(c.pdf_path)}>
                              PDF
                            </Button>
                          )}
                          {c.xml_path && (
                            <Button variant="plain" onClick={() => openCfdiFile(c.xml_path)}>
                              XML
                            </Button>
                          )}
                        </InlineStack>
                      ))}
                    </BlockStack>
                  )}
                  {t.status === "pending_cfdi" && (
                    <Text tone="subdued" variant="bodySm">
                      {notYetInvoiceable(t)
                        ? `Facturable desde el ${shortDate(new Date(`${t.invoice_available_from}T12:00:00`))} · antes del ${shortDate(cfdiDeadline(t))}`
                        : `Factura antes del ${shortDate(cfdiDeadline(t))}`}
                    </Text>
                  )}
                  {t.review_reason && ["needs_review", "rejected", "expired"].includes(t.status) && (
                    <Text tone={t.status === "needs_review" ? "caution" : "subdued"} variant="bodySm">
                      {t.review_reason}
                    </Text>
                  )}
                  {t.status === "approved" && t.auto_approved && (
                    <Text tone="subdued" variant="bodySm">Aprobado automáticamente</Text>
                  )}
                  {t.status === "approved" && t.deductible === false && t.no_cfdi_reason && (
                    <Text tone="subdued" variant="bodySm">{`Sin factura: ${t.no_cfdi_reason}`}</Text>
                  )}
                  {t.status === "pending_cfdi" && t.invoice_mode === "portal" && (
                    <InlineStack gap="200" blockAlign="center">
                      {t.ai?.invoice_url && (
                        <Button
                          variant="plain"
                          url={/^https?:/i.test(t.ai.invoice_url) ? t.ai.invoice_url : `https://${t.ai.invoice_url}`}
                          external
                        >
                          Abrir portal
                        </Button>
                      )}
                      {t.invoice_requested_at ? (
                        <Text tone="subdued" variant="bodySm">
                          {`Solicitada ${new Date(t.invoice_requested_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}`}
                        </Text>
                      ) : (
                        canEdit && (
                          <Button
                            variant="plain"
                            onClick={() => act("mark_ticket_invoice_requested", { p_ticket: t.id, p_requested: true })}
                          >
                            Marcar solicitada
                          </Button>
                        )
                      )}
                    </InlineStack>
                  )}
                  {t.status === "pending_cfdi" && canEdit && (
                    <Button
                      variant="plain"
                      onClick={() =>
                        act("set_ticket_invoice_mode", {
                          p_ticket: t.id,
                          p_mode: t.invoice_mode === "portal" ? "on_site" : "portal",
                        })
                      }
                    >
                      {t.invoice_mode === "portal" ? "Cambiar a: factura en caja" : "Cambiar a: factura en portal"}
                    </Button>
                  )}
                </BlockStack>
                <div style={{ marginLeft: "auto" }}>
                  <BlockStack gap="100" inlineAlign="end">
                    <Text fontWeight="semibold">{t.total != null ? formatPrice(t.total) : "—"}</Text>
                    {t.status === "needs_review" && canReview && (
                      <InlineStack gap="100" wrap={false}>
                        <Button size="slim" loading={busy} onClick={() => decide(t, true)}>
                          Aprobar
                        </Button>
                        <Button size="slim" tone="critical" variant="plain" onClick={() => setRejecting(t)}>
                          Rechazar
                        </Button>
                      </InlineStack>
                    )}
                    {t.status === "pending_cfdi" && canReview && (
                      <Button
                        size="slim"
                        loading={busy}
                        onClick={() => act("approve_ticket_before_cfdi", { p_ticket: t.id })}
                      >
                        Pagar antes de la factura
                      </Button>
                    )}
                    {["pending_cfdi", "needs_review", "expired"].includes(t.status) &&
                      !t.cfdi_uuid &&
                      canReview && (
                        <Button size="slim" variant="plain" onClick={() => setNoCfdi(t)}>
                          Aprobar sin factura
                        </Button>
                      )}
                  </BlockStack>
                </div>
              </InlineStack>
            ))}
          </BlockStack>
        )}
      </BlockStack>
      <Modal
        open={!!noCfdi}
        onClose={() => setNoCfdi(null)}
        title={noCfdi ? `Aprobar sin factura ${formatPrice(noCfdi.total)}` : ""}
        primaryAction={{
          content: "Aprobar como no deducible",
          loading: busy,
          disabled: reason.trim().length < 5,
          onAction: approveNoCfdi,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setNoCfdi(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text tone="subdued">
              Se reembolsa al trasladista en la siguiente liquidación, pero queda como gasto
              no deducible.
            </Text>
            <TextField
              label="¿Por qué se reembolsa sin factura?"
              autoComplete="off"
              value={reason}
              onChange={setReason}
              placeholder="Ej. Puesto de carretera que no factura"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Rechazar gasto"
        primaryAction={{
          content: "Rechazar",
          destructive: true,
          loading: busy,
          disabled: reason.trim().length < 5,
          onAction: () => decide(rejecting, false),
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setRejecting(null) }]}
      >
        <Modal.Section>
          <TextField
            label="Motivo (lo verá el trasladista)"
            autoComplete="off"
            value={reason}
            onChange={setReason}
          />
        </Modal.Section>
      </Modal>
    </Card>
  );
}

export default TrasladoTicketsCard;
